import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { useSection } from '../useSection';
import { useDragReorder } from '../useDragReorder';
import { authFetch } from '../../auth';
import { APP_FAQS } from '../seed';

const API = '/api/cms/app-chapters';

function RefBlockList({ blocks, ci, onReorder, onUpdHeading, onUpdItem, onAddItem, onDelItem, onDelBlock }) {
  const { containerRef, dragProps, dropProps, cardClass } = useDragReorder((from, to) => onReorder(ci, from, to));
  return (
    <div ref={containerRef}>
      {(blocks || []).map((b, bi) => {
        const { onMouseDown, onDragStart, onDragEnd } = dragProps(bi);
        return (
          <div
            className={`ref-block ${cardClass(bi)}`}
            key={bi}
            data-flip={String(bi)}
            onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e); }}
            onDragStart={(e) => { e.stopPropagation(); onDragStart(e); }}
            onDragEnd={onDragEnd}
            {...dropProps(bi)}
          >
            <span className="drag-handle" title="블록을 드래그하여 순서 변경">⠿</span>
            <div className="ref-block-head">
              <Editable as="h5" value={b.heading} onChange={(t) => onUpdHeading(ci, bi, t)} />
              <button type="button" className="row-del-btn" title="참고블록 삭제" onClick={() => onDelBlock(ci, bi)}>×</button>
            </div>
            <ul className="ref-list">
              {(b.items || []).map((it, ii) => (
                <li key={ii}>
                  <Editable value={it} onChange={(t) => onUpdItem(ci, bi, ii, t)} />
                  <button type="button" className="row-del-btn sm" title="항목 삭제" onClick={() => onDelItem(ci, bi, ii)}>×</button>
                </li>
              ))}
            </ul>
            <button type="button" className="row-add-btn sm" onClick={() => onAddItem(ci, bi)}>+ 항목 추가</button>
          </div>
        );
      })}
    </div>
  );
}

export default function Applications({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [openCh, setOpenCh] = useState({}); // 챕터별 펼침 상태(기본 접힘)
  const toggleCh = (key) => setOpenCh((o) => ({ ...o, [key]: !o[key] }));
  const ref = useRef([]);
  const delRef = useRef([]);      // 저장 시 삭제할 챕터 intNum(삭제 예약)
  const dirtyRef = useRef(false);
  const { data: ui, setField, saveSection, isDirty: uiIsDirty } = useSection('applications', 'ui', 1);
  const { data: faqData, setField: setFaqField, saveSection: saveFaq, isDirty: faqIsDirty } = useSection('applications', 'faq', 2);
  const faqItems = faqData.items || APP_FAQS;
  const updFaq = (i, f, v) => {
    const next = (faqData.items || APP_FAQS).map((x, idx) => (idx === i ? { ...x, [f]: v } : x));
    setFaqField('items', next);
  };

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?lang=ko`).then((r) => r.json())
      .then((d) => { if (alive) { ref.current = d; setItems(d); } })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  const apply = (fn) => { dirtyRef.current = true; ref.current = fn(ref.current); setItems(ref.current); };
  const updCh = (ci, f, v) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, [f]: v } : c)));
  const updLesson = (ci, li, f, v) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, lessons: c.lessons.map((l, j) => (j === li ? { ...l, [f]: v } : l)) } : c)));
  const updHeading = (ci, bi, v) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, refBlocks: c.refBlocks.map((b, j) => (j === bi ? { ...b, heading: v } : b)) } : c)));
  const updItem = (ci, bi, ii, v) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, refBlocks: c.refBlocks.map((b, j) => (j === bi ? { ...b, items: b.items.map((it, m) => (m === ii ? v : it)) } : b)) } : c)));

  // 드래그앤드롭 순서 변경 — sort 재부여(저장 시 DB 반영 → 홈페이지 정렬)
  const reorder = (from, to) => {
    dirtyRef.current = true;
    const arr = [...ref.current];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    ref.current = arr.map((x, i) => ({ ...x, sort: i + 1 }));
    setItems(ref.current);
  };
  const { containerRef, dragProps, dropProps, cardClass } = useDragReorder(reorder);

  const reorderRefBlock = (ci, from, to) => apply((cs) => cs.map((c, i) => {
    if (i !== ci) return c;
    const arr = [...(c.refBlocks || [])];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    return { ...c, refBlocks: arr };
  }));

  const addChapter = () => {
    const nc = {
      intNum: null, _tmpId: `tmp-${Date.now()}`, title: '새 분야', meta: '', sort: ref.current.length + 1, homeShow: false,
      lessons: [{ title: '', desc: '', tag: '' }],
      refBlocks: [{ heading: '', items: [''] }],
    };
    apply((cs) => [...cs, nc]);
  };
  // 삭제 예약: 저장된 챕터는 저장 시 DELETE, 미저장 챕터는 로컬 제거(저장 전이면 되돌릴 수 있음)
  const delChapter = (ci) => {
    const c = ref.current[ci];
    if (c.intNum != null) {
      if (!window.confirm('이 분야(카드)를 삭제할까요? (저장 시 반영)')) return;
      delRef.current.push(c.intNum);
    }
    apply((cs) => cs.filter((_, i) => i !== ci));
  };

  // 레슨 추가/삭제
  const addLesson = (ci) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, lessons: [...(c.lessons || []), { title: '', desc: '', tag: '' }] } : c)));
  const delLesson = (ci, li) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, lessons: (c.lessons || []).filter((_, j) => j !== li) } : c)));
  // 참고블록 추가/삭제
  const addRefBlock = (ci) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, refBlocks: [...(c.refBlocks || []), { heading: '', items: [''] }] } : c)));
  const delRefBlock = (ci, bi) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, refBlocks: (c.refBlocks || []).filter((_, j) => j !== bi) } : c)));
  // 참고블록 항목(li) 추가/삭제
  const addItem = (ci, bi) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, refBlocks: (c.refBlocks || []).map((b, j) => (j === bi ? { ...b, items: [...(b.items || []), ''] } : b)) } : c)));
  const delItem = (ci, bi, ii) => apply((cs) => cs.map((c, i) => (i === ci ? { ...c, refBlocks: (c.refBlocks || []).map((b, j) => (j === bi ? { ...b, items: (b.items || []).filter((_, m) => m !== ii) } : b)) } : c)));

  const save = useCallback(async () => {
    for (const id of delRef.current) {
      const res = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`적용분야 삭제 실패 (${res.status})`);
    }
    delRef.current = [];
    for (const c of ref.current) {
      const res = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intNum: c.intNum, title: c.title, meta: c.meta, link: c.link || null, sort: c.sort, homeShow: c.homeShow,
          lessons: (c.lessons || []).map((l) => ({ title: l.title, desc: l.desc, tag: l.tag })),
          refBlocks: (c.refBlocks || []).map((b) => ({ heading: b.heading, items: b.items || [] })),
        }),
      });
      if (!res.ok) throw new Error(`적용분야 저장 실패 (${res.status})`);
    }
    await saveSection();
    await saveFaq();
    dirtyRef.current = false;
  }, [saveSection, saveFaq]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current || uiIsDirty() || faqIsDirty(), [uiIsDirty, faqIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (items === null) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  return (
    <>
      <div className="course-hero">
        <img src="https://images.squarespace-cdn.com/content/v1/69b4331f721b4f65d591a841/1722380710.748033-KSEUMKIWCCRSHMKEJNCV/imgg-od3-ojqabo6y.png" alt="적용 분야" />
      </div>
      <div className="course-main">
        <Editable as="h1" value={ui.title || '적용 분야'} onChange={(t) => setField('title', t)} />
        <Editable as="p" value={ui.desc || 'AceTec의 플랫폼이 국방, 철도 교통, 통신, 산업 분야 — 가동률과 신뢰성이 결코 타협될 수 없는 모든 현장 — 에서 어떻게 활용되는지 확인해 보세요.'} onChange={(t) => setField('desc', t)} />
      </div>

      <div className="ch-list" role="region" aria-label="적용 분야" ref={containerRef}>
        {items.map((c, ci) => {
          const cKey = c.intNum != null ? `ch-${c.intNum}` : c._tmpId;
          const isOpen = !!openCh[cKey];
          return (
          <div className={`ch-item ${isOpen ? 'open' : ''} ${cardClass(ci)}`} key={cKey} data-flip={cKey} {...dragProps(ci)} {...dropProps(ci)}>
            <span className="drag-handle" title="카드를 드래그하여 순서 변경">⠿</span>
            <button type="button" className="slide-del-btn" title="분야 삭제" onClick={() => delChapter(ci)}>×</button>
            <div className="ch-head">
              <div>
                <Editable as="h3" value={c.title} onChange={(t) => updCh(ci, 'title', t)} />
                <Editable as="small" className="chapter-meta" value={c.meta} onChange={(t) => updCh(ci, 'meta', t)} />
              </div>
              <button type="button" className="ch-toggle" title={isOpen ? '접기' : '펼치기'} onClick={() => toggleCh(cKey)}>{isOpen ? '▾' : '▸'}</button>
            </div>
            <div className="ch-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <label style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>제품 링크</label>
                <input
                  style={{ flex: 1, fontSize: 13, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4 }}
                  value={c.link || ''}
                  placeholder="/products/military"
                  onChange={(e) => updCh(ci, 'link', e.target.value || null)}
                />
              </div>
              {(c.lessons || []).map((l, li) => (
                <div className="lesson" key={li}>
                  <div>
                    <Editable as="h4" value={l.title} onChange={(t) => updLesson(ci, li, 'title', t)} />
                    <Editable as="p" value={l.desc} onChange={(t) => updLesson(ci, li, 'desc', t)} />
                  </div>
                  <button type="button" className="row-del-btn" title="레슨 삭제" onClick={() => delLesson(ci, li)}>×</button>
                </div>
              ))}
              <button type="button" className="row-add-btn" onClick={() => addLesson(ci)}>+ 레슨 추가</button>

              <RefBlockList
                blocks={c.refBlocks}
                ci={ci}
                onReorder={reorderRefBlock}
                onUpdHeading={updHeading}
                onUpdItem={updItem}
                onAddItem={addItem}
                onDelItem={delItem}
                onDelBlock={delRefBlock}
              />
              <button type="button" className="row-add-btn" onClick={() => addRefBlock(ci)}>+ 참고블록 추가</button>
            </div>
          </div>
          );
        })}
        <button type="button" className="card-add-tile" onClick={addChapter}>+ 분야(카드) 추가</button>
      </div>

      <section className="faq-sec">
        <div className="faq-grid">
          <Editable as="h2" value={ui.faqTitle || '자주 묻는 질문'} onChange={(t) => setField('faqTitle', t)} />
          <div>
            <div className="acc" role="region" aria-label="자주 묻는 질문">
              {faqItems.map((f, i) => (
                <div className="acc-item open" key={i}>
                  <div className="acc-title"><Editable value={f.q} onChange={(t) => updFaq(i, 'q', t)} /><span className="icon" aria-hidden="true">+</span></div>
                  <div className="acc-body"><Editable as="p" value={f.a} onChange={(t) => updFaq(i, 'a', t)} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
